import { Button } from "@/components/ui/baseUi/button"
import { SearchUserModal, type SearchUserModalRef } from "@/components/ui/forms/searchUserInput"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { BoxLeadMergeIcon } from "@images/svgIcons/boxLeads/boxLeadMergeIcon"
import { useBoxLeadMerge } from "@modules/boxLead/hooks/useBoxLeadActions"
import { useBoxLead } from "@modules/boxLead/hooks/useBoxLeadDetails"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@modules/core/utils/i18n"
import { ExploreProfileItemEntity } from "@modules/explore/entities/exploreEntity"
import { HuAvatarCardSidebar } from "@modules/ui/components/huAvatarCardSidebar"
import { LoaderCircle } from "lucide-react"
import { useRef } from "react"

type BoxLeadDetailsHeaderProps = {
  leadId: string
  className?: string
} & React.ComponentProps<"div">

export function BoxLeadDetailsHeader({ leadId, ...props }: BoxLeadDetailsHeaderProps) {
  const { data: leadDetails } = useBoxLead(leadId)
  const { mergeBoxLead, isPending: isPendingMerge } = useBoxLeadMerge()
  const searchUserModal = useRef<SearchUserModalRef>(null)
  const canEditLead = useBoxStaffPermission(BoxStaffPermissions.MANAGE_LEAD)

  const setUserSelected = async (user: ExploreProfileItemEntity) => {
    await mergeBoxLead({ leadId, userId: user.id })
      .then(() => {
        toast.success(word("box.lead.form.input.haveHustleUpAccount.merge.success"), {
          classNames: successToastClassName(),
        })
      })
      .catch(() => {
        toast.error(word("box.lead.form.input.haveHustleUpAccount.merge.error"), {
          classNames: errorToastClassName(),
        })
      })
  }

  return (
    <>
      <HuAvatarCardSidebar
        id={leadDetails.user?.id}
        avatar={leadDetails.user?.avatar ?? undefined}
        firstname={leadDetails.user?.firstname ?? leadDetails.firstname ?? ""}
        lastname={leadDetails.user?.lastname ?? leadDetails.lastname ?? ""}
        username={leadDetails.getUserName()}
        email={leadDetails.email ?? undefined}
        canUseContact={leadDetails.user ? true : false}
        bottomButton={
          !leadDetails.user && canEditLead ? (
            <Button
              isLoading={isPendingMerge}
              variant="outline"
              onClick={() => searchUserModal.current?.open()}
              disabled={isPendingMerge}
            >
              {isPendingMerge ? <LoaderCircle className="animate-spin" /> : <BoxLeadMergeIcon color="currentColor" />}
              {word("box.lead.form.input.haveHustleUpAccount.merge.button")}
            </Button>
          ) : undefined
        }
        {...props}
      />
      <SearchUserModal
        ref={searchUserModal}
        setUserSelected={(user) => {
          void setUserSelected(user)
        }}
        searchDescriptionText={word("box.lead.form.input.haveHustleUpAccount.dialog.searchUser")}
        requestConfirmation
        confirmText={word("global.check")}
        cancelText={word("global.cancel")}
        confirmDescriptionText={word("box.lead.form.input.haveHustleUpAccount.dialog.confirmDescription")}
      />
    </>
  )
}
